/**
 * Per-post JSON (public/data/posts/<slug>.json) fetched once per slug.
 *
 * main.tsx starts the fetch before React mounts on a blog post URL, so the
 * page's first render already has the post instead of replacing the
 * prerendered article with an empty or "not found" state.
 */
const pending = new Map<string, Promise<unknown | null>>();
const settled = new Map<string, unknown | null>();

export function loadBlogPost<T>(slug: string): Promise<T | null> {
  let request = pending.get(slug);
  if (!request) {
    request = fetch(`/data/posts/${encodeURIComponent(slug)}.json`)
      .then((res) => {
        const type = res.headers.get('content-type') || '';
        // An unknown slug may hit the SPA fallback (HTML), not a 404.
        if (!res.ok || !type.includes('json')) return null;
        return res.json() as Promise<{ slug?: string }>;
      })
      .then((post) => (post && post.slug === slug ? post : null))
      .catch((err) => {
        console.error(err);
        pending.delete(slug); // let a later visit retry
        return null;
      });
    request.then((post) => { if (pending.get(slug) === request) settled.set(slug, post); });
    pending.set(slug, request);
  }
  return request as Promise<T | null>;
}

/** The post if its fetch has finished: the post, null (missing), or undefined (not loaded yet). */
export function getLoadedBlogPost<T>(slug: string): T | null | undefined {
  return settled.get(slug) as T | null | undefined;
}
